import React from 'react';
import { Box, TableSortLabel } from '@mui/material';

import {
  Typography,
  Pagination,
  EmptyDataTable,
  CircularProgress,
  Table as MUITable,
  TableBody as MUITableBody,
  TableCell as MUITableCell,
  TableHead as MUITableHead,
  TableRow as MUITableRow,
  TableContainer as MUITableContainer,
} from '@occc-app/design-system';

import { TableProps } from './types';

export const Table = ({
  headers,
  loading,
  rows,
  error,
  paginationProps,
  sortingProps,
}: TableProps) => {
  if (error) {
    return (
      <Box p={4}>
        <Typography color='error' data-testid='something-went-wrong-msg'>
          Something went wrong. Please try again.
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box p={4} display='flex' justifyContent='center' alignItems='center'>
        <CircularProgress />
      </Box>
    );
  }

  if (!rows) {
    return (
      <Box p={4}>
        <Typography color='error' data-testid='no-data-msg'>
          No data. Something went wrong. Please try again.
        </Typography>
      </Box>
    );
  }

  if (rows.length === 0) {
    return <EmptyDataTable />;
  }

  const tableRows = rows.map((row, rowIndex) => {
    const onClick: (event: React.MouseEvent<HTMLTableRowElement>) => void = (event) => {
      event.preventDefault();
      if (row.onClick) {
        row.onClick();
      }
    };

    return (
      <MUITableRow
        key={row.key || rowIndex}
        onClick={onClick}
        sx={{ cursor: row.onClick ? 'pointer' : undefined }}
      >
        {row.fields.map(({ label, value, width, align, sx }, cellIndex) => (
          <MUITableCell
            key={label || cellIndex}
            width={width}
            align={align}
            sx={{
              overflowWrap: 'anywhere',
              ...sx,
            }}
          >
            {value}
          </MUITableCell>
        ))}
      </MUITableRow>
    );
  });

  const tableHeader = headers.map((header, index) => {
    const { label, align, sortingField } = header;
    if (sortingField) {
      return (
        <MUITableCell key={label || index} align={align} sx={{ whiteSpace: 'nowrap' }}>
          <TableSortLabel
            active={sortingField === sortingProps?.field}
            direction={sortingProps?.direction}
            onClick={
              sortingProps?.onClick ? () => sortingProps?.onClick?.(sortingField) : undefined
            }
          >
            {label}
          </TableSortLabel>
        </MUITableCell>
      );
    }
    return (
      <MUITableCell key={label || index} align={align} sx={{ whiteSpace: 'nowrap' }}>
        {label}
      </MUITableCell>
    );
  });

  return (
    <MUITableContainer>
      <MUITable>
        <MUITableHead>
          <MUITableRow>{tableHeader}</MUITableRow>
        </MUITableHead>
        <MUITableBody>{tableRows}</MUITableBody>
      </MUITable>
      {paginationProps && <Pagination {...paginationProps} />}
    </MUITableContainer>
  );
};
